/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as userGet } from 'js/lib/user';

import { typography2 } from '@coursera/cds-core';

import type { ConsumerPageHeader_EnterpriseProgramFragment as EnterpriseProgramFragment } from 'bundles/composable-page-header-consumer/queries/__generated__/ConsumerMyPageHeaderQuery';
import EnterpriseSearchBarV2, {
  EnterpriseSearchBarImpl,
  UnifiedEnterpriseSearchBar,
} from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchBarV2';
import type { SearchScope } from 'bundles/enterprise-learner-search/components/search/types';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';

import _t from 'i18n!nls/composable-page-header-enterprise';

type Props = {
  currentProgram?: Partial<EnterpriseProgramFragment> | null;
  enterprisePrograms?: Partial<EnterpriseProgramFragment>[] | null;
  forceFullWidthMobile?: boolean;
};

const styles = {
  searchBar: css`
    && {
      width: 100%;

      .rc-EnterpriseSearchBarV2 {
        max-width: 480px;
        min-width: 0;
        width: 100%;

        input {
          /* Needs important to override old style that's also set to important. Yuck! */
          font: ${typography2.bodySecondary.font} !important;
          letter-spacing: ${typography2.bodySecondary.letterSpacing};
        }
      }
    }
  `,
};

export function EnterpriseSearchBarLegacyGridItem(props: Props) {
  const { currentProgram, enterprisePrograms, forceFullWidthMobile } = props;

  const enterpriseProductFeatures = useEnterpriseProductFeatures();
  const userId = userGet()?.id;

  const enableSkillsInSearchAndBrowse = enterpriseProductFeatures?.enableSkillsInSearchAndBrowse;
  const shouldShowShortFormContent = enterpriseProductFeatures?.allowShortFormContentBasedOnAudience;

  const thirdPartyOrganizationSlug = currentProgram?.naptimeThirdPartyOrganization?.slug;
  const thirdPartyOrganizationId = currentProgram?.naptimeThirdPartyOrganization?.id;

  const programSlug = currentProgram?.slug;
  const programName = currentProgram?.name;
  const programId = currentProgram?.id;

  const programIds: Array<string> = [];
  const scopes: Array<SearchScope> | undefined = enterprisePrograms
    ?.map((tmp) => {
      const programIdTmp = tmp?.id ?? '';
      programIds.push(programIdTmp);
      const { name: programName0, slug: programSlug0 } = tmp;
      return {
        trackingData: { programSlug: programSlug0 ?? '' },
        pathname: `/programs/${programSlug0}`,
        placeholder: _t("Search in '#{programName}'...", { programName0 }),
        title: programName0 ?? '',
        programIds: [programIdTmp],
      };
    })
    .sort((a, b) => a.placeholder.localeCompare(b.placeholder));
  scopes?.unshift({
    trackingData: { thirdPartyOrgSlug: thirdPartyOrganizationSlug ?? '' },
    pathname: `/programs/all/${thirdPartyOrganizationSlug}`,
    placeholder: _t('Search in all programs you joined...'),
    title: _t('All Learning Programs'),
    programIds,
  });

  if (userId && programSlug && programName && programId && thirdPartyOrganizationId && thirdPartyOrganizationSlug) {
    return (
      <div css={styles.searchBar}>
        <UnifiedEnterpriseSearchBar
          forceFullWidthMobile={forceFullWidthMobile}
          programSlug={programSlug}
          programName={programName}
          thirdPartyOrgSlug={thirdPartyOrganizationSlug}
          thirdPartyOrganizationId={thirdPartyOrganizationId}
          userId={userId}
          programId={programId}
          enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
          shouldShowShortFormContent={shouldShowShortFormContent}
        />
      </div>
    );
  } else if (programId) {
    return (
      <div css={styles.searchBar}>
        <EnterpriseSearchBarV2
          programIds={[programId]}
          enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse ?? false}
          shouldShowShortFormContent={shouldShowShortFormContent}
          programName={programName}
        />
      </div>
    );
  } else if (enterprisePrograms) {
    return (
      <div css={styles.searchBar}>
        <EnterpriseSearchBarImpl
          initialScopeIndex={0}
          scopes={scopes}
          enableSkillsInSearchAndBrowse={!!enableSkillsInSearchAndBrowse}
          shouldShowShortFormContent={shouldShowShortFormContent}
        />
      </div>
    );
  } else {
    return null;
  }
}

export default EnterpriseSearchBarLegacyGridItem;
